import { trackEvent } from '@finn-no/pulse-sdk';
import type { TrackerData } from '@finn-no/pulse-sdk/dist/types';
import { safeParseISO } from 'shared/util/datetime-fns';
import type { Money } from 'shared/util/money';
import type { RentalStatusPageType } from '@/domain/transaction';
import type { PaymentOption } from '@/models/rentalStatus';

export const trackCreateRentalRequestClick = (
    itemId: number,
    startDate: string,
    endDate: string,
    guests: number,
    price?: Money,
    country?: string | null,
    locality?: string | null,
) => {
    trackEvent({
        type: 'Click',
        intent: 'Send',
        name: 'Request to rent property',
        object: {
            id: 'create_rental_request',
            type: 'UIElement',
            elementType: 'button',
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${itemId}`,
                    type: 'ClassifiedAd',
                    contentId: `${itemId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: 'finntravel',
                    type: 'Travel',
                    travelType: 'holiday_rental',
                    departureDate: safeParseISO(startDate)?.toISOString() ?? undefined,
                    returnDate: safeParseISO(endDate)?.toISOString() ?? undefined,
                    numberOfAdults: guests,
                    // Due to legacy definitions in Pulse, `destination` is used instead of `arrivalCountry`
                    destination: country ?? undefined,
                    arrivalCity: locality ?? undefined,
                    totalPrice: price?.value,
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

// TJVR_ev04, TJVR_ev05
export const trackClickMessageLink = (bookingReference: string, adId: number, pageType: RentalStatusPageType, position: string) => {
    trackEvent({
        type: 'Click',
        intent: 'Send',
        name: `Send message to ${pageType === 'host' ? 'guest' : 'host'}`,
        object: {
            id: 'send_message',
            type: 'UIElement',
            elementType: 'button',
            position,
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${adId}`,
                    type: 'ClassifiedAd',
                    contentId: `${adId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: bookingReference,
                    type: 'ProcessFlow',
                    name: 'Rental transaction',
                    contractId: bookingReference,
                    isAdvertiser: pageType === 'host',
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

// TJVR_ev06, TJVR_ev07, TJVR_ev08, TJVR_ev09, TJVR_ev10, TJVR_ev11, TJVR_ev14, TJVR_ev15, TJVR_ev16
export const trackUpdateRentalClick = (bookingReference: string, adId: number, intent: TrackerData['intent'], name: string) => {
    trackEvent({
        type: 'Click',
        intent,
        name,
        object: {
            id: 'accept_reject_rental',
            type: 'UIElement',
            elementType: 'button',
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${adId}`,
                    type: 'ClassifiedAd',
                    contentId: `${adId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: bookingReference,
                    type: 'ProcessFlow',
                    name: 'Rental transaction',
                    contractId: bookingReference,
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

// TJVR_ev12, TJVR_ev13
export const trackPaymentMethodClick = (bookingReference: string, adId: number, paymentOption: PaymentOption) => {
    trackEvent({
        type: 'Click',
        intent: 'Request',
        name: `Continue with ${paymentOption === 'VIPPS' ? 'vipps' : 'card'}`,
        object: {
            id: 'payment_method',
            type: 'UIElement',
            elementType: 'button',
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${adId}`,
                    type: 'ClassifiedAd',
                    contentId: `${adId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: bookingReference,
                    type: 'ProcessFlow',
                    name: 'Rental transaction',
                    contractId: bookingReference,
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackCreateInsuranceClaimClick = (bookingReference: string, adId: number, intent: TrackerData['intent']) => {
    trackEvent({
        type: 'Click',
        intent,
        name: 'Host creates insurance claim',
        object: {
            id: 'create_insurance_claim',
            type: 'UIElement',
            elementType: 'button',
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${adId}`,
                    type: 'ClassifiedAd',
                    contentId: `${adId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: bookingReference,
                    type: 'ProcessFlow',
                    name: 'Rental transaction',
                    contractId: bookingReference,
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackCreateDisputeClick = (bookingReference: string, adId: number, intent: TrackerData['intent'], disputeType: string) => {
    trackEvent({
        type: 'Click',
        intent,
        name: 'Guest report a problem',
        object: {
            id: 'guest_report_problem',
            type: 'UIElement',
            elementType: 'Button',
            name: disputeType,
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${adId}`,
                    type: 'ClassifiedAd',
                    contentId: `${adId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: bookingReference,
                    type: 'ProcessFlow',
                    name: 'Rental transaction',
                    contractId: bookingReference,
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};

export const trackShowContactInfo = (bookingReference: string, adId: number, pageType: RentalStatusPageType) => {
    trackEvent({
        type: 'Click',
        intent: 'Show',
        name: `Show ${pageType === 'host' ? 'guest' : 'host'} contact info`,
        object: {
            id: 'show_contact_info',
            type: 'UIElement',
            elementType: 'Expandable box',
            category: 'travel > holiday rental',
            items: [
                {
                    id: `${adId}`,
                    type: 'ClassifiedAd',
                    contentId: `${adId}`,
                    name: 'n/a',
                    category: 'travel > holiday rental',
                },
                {
                    id: bookingReference,
                    type: 'ProcessFlow',
                    name: 'Rental transaction',
                    contractId: bookingReference,
                    category: 'travel > holiday rental',
                },
            ],
        },
        vertical: {
            name: 'travel',
            subVertical: 'holiday_rental',
        },
    });
};
